import React, { useMemo } from "react";

// Bright, varied bloom colours for multi-colour flora across the world.
export const FLOWER_COLORS = ["#FFCE24", "#FFFFFF", "#FF5FA2", "#FF8A3D", "#A06CFF", "#FF5A4D", "#46D6E0", "#FF3D7F"];

function makeRng(seed) {
  let s = (seed >>> 0) || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

// A small, LOW cluster of wildflowers + grass tufts. Max height ~0.45 so it can sit
// right next to a building without ever hiding a door. Deterministic via `seed`.
export function FloraCluster({ position = [0, 0, 0], scale = 1, seed = 1, night = false, count = 4, spread = 0.5 }) {
  const items = useMemo(() => {
    const r = makeRng(seed);
    const arr = [];
    const n = count;
    for (let i = 0; i < n; i++) {
      const ang = r() * Math.PI * 2;
      const rad = r() * spread;
      arr.push({
        type: "flower",
        x: Math.cos(ang) * rad,
        z: Math.sin(ang) * rad,
        h: 0.22 + r() * 0.2,
        bloom: 0.07 + r() * 0.055,
        col: FLOWER_COLORS[Math.floor(r() * FLOWER_COLORS.length)],
        lean: (r() - 0.5) * 0.3,
      });
    }
    const g = 2 + Math.floor(r() * 2);
    for (let i = 0; i < g; i++) {
      const ang = r() * Math.PI * 2;
      const rad = r() * spread * 1.15;
      arr.push({ type: "tuft", x: Math.cos(ang) * rad, z: Math.sin(ang) * rad, h: 0.16 + r() * 0.14 });
    }
    return arr;
  }, [seed, count, spread]);

  const stem = night ? "#3E5A38" : "#4F7A3C";
  const tuftC = night ? "#34502F" : "#5C8A44";

  return (
    <group position={position} scale={scale}>
      {items.map((it, i) =>
        it.type === "flower" ? (
          <group key={i} position={[it.x, 0, it.z]} rotation={[it.lean, 0, it.lean]}>
            <mesh position={[0, it.h / 2, 0]}>
              <cylinderGeometry args={[0.02, 0.028, it.h, 5]} />
              <meshStandardMaterial color={stem} roughness={1} />
            </mesh>
            <mesh position={[0, it.h, 0]} castShadow>
              <icosahedronGeometry args={[it.bloom, 0]} />
              <meshStandardMaterial color={it.col} emissive={it.col} emissiveIntensity={night ? 0.5 : 0.12} roughness={0.7} flatShading />
            </mesh>
          </group>
        ) : (
          <mesh key={i} position={[it.x, it.h * 0.5, it.z]} castShadow>
            <icosahedronGeometry args={[it.h * 0.7, 0]} />
            <meshStandardMaterial color={tuftC} roughness={1} flatShading />
          </mesh>
        )
      )}
    </group>
  );
}
